import { z } from 'zod'

/**
 * The theory-vocabulary lexicon is the single source of pedagogical copy for
 * every music-theory learning surface (cadence chip, progression overlay,
 * modal glow, suggestion explainer, chord popup). Each entry carries copy at
 * three registers; the active register is selected at the use site from
 * `userConfig.musicPreferences.theoryLevel`.
 *
 * The schema lives in its own module to keep `lexicon-entries.ts` free of any
 * import that would close a cycle back through `lexicon.ts`.
 *
 * Spec: docs/superpowers/specs/2026-05-16-music-theory-learning-phase-1-design.md
 */
export const lexiconEntrySchema = z.object({
  /** Stable id referenced by analyzers and UI surfaces. */
  id: z.string().min(1),
  category: z.enum([
    'chord-quality',
    'function',
    'borrowed',
    'cadence',
    'mode',
    'progression',
    'voice-leading',
    'transition',
    'melody',
    'tension',
    'substitution',
    'crosslink',
    // ───── Ear Training Suite v1 additions (Phase 2 Tier 2) ─────
    'interval',
    'quality',
    'ear-training',
  ]),
  displayName: z.string().min(1),
  /** Compact display variant for chips and small surfaces. */
  displayNameShort: z.string().min(1),
  /**
   * 1-4 hint for ordering and tooltip-density decisions. Not a level gate —
   * see `theoryLevel` in `userConfigSchema` for the gating mechanism.
   */
  pedagogicalLevel: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  copy: z.object({
    beginner: z.string().min(1),
    intermediate: z.string().min(1),
    advanced: z.string().min(1),
  }),
  /**
   * Per-level short chip text used by `WindowTag` (and any future tag surface).
   * Required for every entry — UX (Joni §2.1) calls for level-dependent tag
   * text (e.g., `resolving` → `authentic` → `V → I`) so the chip stays prose-
   * forward at beginner and theory-forward at advanced. `displayNameShort` is
   * the editorial short name, not a level-resolved tag.
   */
  tagLabel: z.object({
    beginner: z.string().min(1),
    intermediate: z.string().min(1),
    advanced: z.string().min(1),
  }),
  /** Optional genre hints — drives genre-aware filtering in later milestones. */
  genreTags: z.array(z.string()).optional(),
  /** Optional reference to an audio-demo entity id — wired in later milestones. */
  audioDemoRef: z.string().optional(),
})

export type LexiconEntry = z.infer<typeof lexiconEntrySchema>
